import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { debounce } from 'lodash-es'
import { deviceType, isTablet } from 'react-device-detect'

import { loadInitialData } from '~/@app/init/loadInitialData'
import { webviewService } from '~/@common/services'
import authService from '~/@common/services/auth.service'
import { localStorageService } from '~/@common/services/storage.service'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import maxios from '~/@common/utils/maxios'
import { parseBooleanStringToBoolean } from '~/@common/utils/parsers'

import { decodeMigrationHash } from './migrationHash'

//     1. apps/mathflat-student/src/@app/init/initialize.ts — student도메인에 베이직 소스라면 들어올 가능성 있어서 배제 못함 legacy 호환
//     2. apps/mathflat-student/src/@common/integration/handleStudentUpLogic.ts — 통합 student-up 에서 통합 student 가는 흐름
export const migrateFromHashParam = async (urlParams: URLSearchParams): Promise<void> => {
  const ciphertext = urlParams.get('hash')
  if (!ciphertext) throw new Error('hash param missing')

  const decoded = decodeMigrationHash(ciphertext)
  const { accessToken, refreshToken, academyId, studentId, isPersist } = decoded

  if (!accessToken || !refreshToken || !academyId || !studentId) {
    throw new Error('hash payload incomplete')
  }

  // ─── hash 외부 query string — 베이직 호환 부가 동작 ────────────────────────
  const isInitialPasswordParam = urlParams.get('isInitialPassword')
  const isInitialPassword =
    isInitialPasswordParam === null ? null : parseBooleanStringToBoolean(isInitialPasswordParam)

  const isPasswordChangedRequiredParam = urlParams.get('isPasswordChangedRequired')
  const isPasswordChangedRequired =
    isPasswordChangedRequiredParam === null
      ? null
      : parseBooleanStringToBoolean(isPasswordChangedRequiredParam)

  const xPlatform = urlParams.get('xPlatform')
  if (xPlatform) {
    maxios.defaults.headers.common['x-platform'] = xPlatform
  }

  // ─── 사용자 식별자 + 비밀번호 플래그 ────────────────────────────────────
  localStorageService.update({
    user: {
      academyId,
      studentId: studentId as `I${string}`,
      isInitialPassword,
      ...(isPasswordChangedRequired !== null && { isPasswordChangedRequired }),
    },
  })

  // ─── 토큰 저장 ──────────────────────────────────────────────────────────
  // ⚠ authService.loginFromBasicStudentApp 는 isKeepSignInChecked=true 일 때만 useLocalStorage 호출.
  //   false 시 useSessionStorage 호출 책임이 호출측에 있음 — 이전 진입의 mode 가 그대로 유지되는 문제 방지.
  if (!isPersist) {
    jwtTokenService.useSessionStorage()
  }
  await authService.loginFromBasicStudentApp({
    accessToken,
    refreshToken,
    isKeepSignInChecked: !!isPersist,
  })

  // ─── ver / orientation — 베이직 호환 부가 동작 ─────────────────────────
  const ver = urlParams.get('ver')
  if (ver) {
    localStorageService.update({ device: { ver } })

    if (!isTablet && deviceType === 'mobile') {
      webviewService.requestOrientation('portrait')
    } else {
      webviewService.requestOrientation('landscape')

      const handleResize = debounce(() => {
        webviewService.requestOrientation(
          window.innerWidth < mediaQuery.breakPoint.tablet ? 'portrait' : 'landscape',
        )
      }, 300)
      window.addEventListener('resize', handleResize)
    }
  }

  // ─── 사용자 메타 / 권한 로드 ────────────────────────────────────────────
  await loadInitialData(studentId as `I${string}`)
}
